/**
 * Анимация знака «Провал уровня» (вариант 2a макета, ТЗ §111 п. 8).
 *
 * Три нижних бруска гаснут в случайном порядке; всё, что выше погасшего,
 * падает на его место; затем бруски появляются снизу вверх и выталкивают всё
 * обратно, а верхний покачивается по X. Игра идёт на Web Animations API:
 * `transform` и `opacity` не трогают раскладку.
 *
 * Функция написана заново по описанию хендоффа, а не скопирована. Зависимости
 * (`animate`, `random`, `reducedMotion`) передаются параметром, чтобы тест не
 * зависел от браузера и случайности.
 */

/** Полная длительность игры, мс. */
export const KELV_MARK_DURATION_MS = 1200;

/** Сдвиг на высоту бруска плюс зазор: нижний, средний, верхний. */
const SLOT = [21, 16, 15] as const;
/** Первое исчезновение и шаг между исчезновениями, мс. */
const FIRST_EVENT_MS = 40;
const EVENT_STEP_MS = 150;
/** Падение: ускорение вниз; посадка на место после выталкивания; перелёт уровня. */
const FALL_EASING = "cubic-bezier(.55,0,1,.45)";
const SETTLE_EASING = "cubic-bezier(.2,.8,.3,1)";
/** Индекс уровня (верхней фирменной полосы) среди брусков. */
const LEVEL_INDEX = 3;
/** Сдвиги по X при встряске уровня и моменты, мс. */
const SHAKE: ReadonlyArray<readonly [x: number, atMs: number]> = [
  [-5, 780],
  [4, 850],
  [-2.5, 920],
  [1, 990],
];

export interface KelvMarkDeps<T> {
  animate(element: T, keyframes: Keyframe[], options: { duration: number }): unknown;
  random(): number;
  reducedMotion(): boolean;
}

const browserDeps: KelvMarkDeps<Element> = {
  animate: (element, keyframes, options) => element.animate(keyframes, options),
  random: () => Math.random(),
  reducedMotion: () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
};

function translate(x: number, y: number): string {
  return `translate(${x}px,${y}px)`;
}

/** Доля полной длительности; кадры за её пределом прижимаются к концу. */
function offsetAt(ms: number): number {
  return Math.min(1, ms / KELV_MARK_DURATION_MS);
}

/** Случайная перестановка индексов нижних брусков. */
function shuffledOrder(random: () => number): number[] {
  const order = [0, 1, 2];
  for (let index = order.length - 1; index > 0; index -= 1) {
    const swapWith = Math.floor(random() * (index + 1));
    const held = order[index];
    const other = order[swapWith];
    if (held === undefined || other === undefined) {
      continue;
    }
    order[index] = other;
    order[swapWith] = held;
  }
  return order;
}

function transformFrames(
  elementIndex: number,
  events: ReadonlyArray<{ vanished: number; at: number }>,
): Keyframe[] {
  const frames: Keyframe[] = [{ transform: translate(0, 0), offset: 0 }];
  let dy = 0;
  for (const { vanished, at } of events) {
    if (elementIndex <= vanished) {
      continue;
    }
    const slot = SLOT[vanished];
    if (slot === undefined) {
      continue;
    }
    const to = dy + slot;
    frames.push(
      { transform: translate(0, dy), offset: offsetAt(at + 70), easing: FALL_EASING },
      { transform: translate(0, to), offset: offsetAt(at + 150) },
      { transform: translate(0, to - 2), offset: offsetAt(at + 180) },
      { transform: translate(0, to), offset: offsetAt(at + 220) },
    );
    dy = to;
  }
  frames.push({ transform: translate(0, dy), offset: offsetAt(560), easing: SETTLE_EASING });
  if (elementIndex === LEVEL_INDEX) {
    frames.push({ transform: translate(0, -7), offset: offsetAt(700), easing: "ease-out" });
    for (const [x, atMs] of SHAKE) {
      frames.push({ transform: translate(x, 0), offset: offsetAt(atMs) });
    }
  } else {
    frames.push({ transform: translate(0, dy === 0 ? 0 : -1.5), offset: offsetAt(680) });
  }
  frames.push({ transform: translate(0, 0), offset: 1 });
  return frames;
}

function opacityFrames(vanished: number, at: number): Keyframe[] {
  return [
    { opacity: 1, offset: 0 },
    { opacity: 1, offset: offsetAt(at) },
    { opacity: 0, offset: offsetAt(at + 80) },
    { opacity: 0, offset: offsetAt(560 + vanished * 35) },
    { opacity: 1, offset: offsetAt(620 + vanished * 35) },
    { opacity: 1, offset: 1 },
  ];
}

/**
 * Запускает игру на четырёх брусках: `rects` — нижний, средний, верхний, уровень.
 * Возвращает длительность игры в мс; при «уменьшить движение» — 0 и без вызовов
 * `animate`.
 */
export function playKelvMark<T>(rects: readonly T[], deps: KelvMarkDeps<T>): number {
  const { animate, random, reducedMotion } = deps;
  if (reducedMotion()) {
    return 0;
  }

  const events = shuffledOrder(random).map((vanished, index) => ({
    vanished,
    at: FIRST_EVENT_MS + index * EVENT_STEP_MS,
  }));
  const options = { duration: KELV_MARK_DURATION_MS };

  rects.forEach((rect, elementIndex) => {
    animate(rect, transformFrames(elementIndex, events), options);
  });
  for (const { vanished, at } of events) {
    const rect = rects[vanished];
    if (rect !== undefined) {
      animate(rect, opacityFrames(vanished, at), options);
    }
  }
  return KELV_MARK_DURATION_MS;
}

/** То же на настоящих элементах: Web Animations API, `Math.random`, `prefers-reduced-motion`. */
export function playKelvMarkInBrowser(rects: readonly Element[]): number {
  return playKelvMark(rects, browserDeps);
}

export interface KelvMarkGuard {
  /** Запускает `play`, если прошлая игра закончилась; `false` — повтор отклонён. */
  run(play: () => number): boolean;
  /** Снимает блокировку: при размонтировании знака. */
  reset(): void;
}

/**
 * Блокировка повтора по времени: пока не прошло возвращённое игрой число
 * миллисекунд, новый запуск отклоняется. Таймера нет, а значит и чистить при
 * размонтировании нечего, кроме самой отметки.
 */
export function createKelvMarkGuard(now: () => number): KelvMarkGuard {
  let busyUntil = Number.NEGATIVE_INFINITY;
  return {
    run(play) {
      if (now() < busyUntil) {
        return false;
      }
      const duration = play();
      busyUntil = now() + duration;
      return true;
    },
    reset() {
      busyUntil = Number.NEGATIVE_INFINITY;
    },
  };
}
